/**
 * Rider trail map — OSM/Leaflet rendition of the order's rider GPS trail.
 *
 *   - Tiles: OpenStreetMap (no API key required).
 *   - Polyline: full set of `OrderRiderLogs` rows from
 *     `/staff/api/rider-logs/{id}/`.
 *   - Markers: store (origin), customer (destination), trail start, trail
 *     end, and any "current" rider position.
 *
 * The component is intentionally side-effecty (icon images shipped by
 * `leaflet` resolve to broken URLs under bundlers): we patch the default
 * marker once on import.
 */
import { useEffect, useMemo } from 'react';
import { MapContainer, Marker, Polyline, Popup, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Leaflet ships PNG markers as relative URLs that don't survive a Vite
// bundle. Resolve them via the bundler so they render in dev + prod.
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

const DefaultIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

function dotIcon(color: string, label?: string): L.DivIcon {
  return L.divIcon({
    className: 'rider-trail-dot',
    html: `<span style="
      display:inline-block;
      width:14px;height:14px;border-radius:9999px;
      background:${color};
      box-shadow:0 0 0 2px white, 0 1px 4px rgba(0,0,0,.35);
    "></span>${
      label
        ? `<span style="
            position:absolute;left:18px;top:-2px;
            font-size:10px;font-weight:600;color:#0f172a;
            padding:1px 6px;border-radius:4px;background:white;
            box-shadow:0 1px 3px rgba(0,0,0,.2);white-space:nowrap;
          ">${label}</span>`
        : ''
    }`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  });
}

export interface RiderTrailPoint {
  latitude: number;
  longitude: number;
  timestamp?: string | null;
}

export interface RiderTrailMapProps {
  /** GPS pings, oldest → newest (or any order — we just draw a polyline). */
  trail: RiderTrailPoint[];
  /** Store (origin) coordinates and label. */
  store?: { latitude: number; longitude: number; label?: string } | null;
  /** Customer (destination) coordinates and label. */
  customer?: { latitude: number; longitude: number; label?: string } | null;
  /** Optional override height. */
  className?: string;
  height?: number | string;
}

function FitBounds({ points }: { points: L.LatLngExpression[] }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0] as L.LatLngTuple, 16);
      return;
    }
    const bounds = L.latLngBounds(points as L.LatLngTuple[]);
    map.fitBounds(bounds, { padding: [24, 24], maxZoom: 17 });
  }, [map, points]);
  return null;
}

export function RiderTrailMap({
  trail,
  store,
  customer,
  className,
  height = 320,
}: RiderTrailMapProps) {
  // Sort the trail oldest -> newest using timestamp when present so the
  // polyline draws in the actual ride direction. If timestamps are
  // missing we fall back to the input order.
  const orderedTrail = useMemo(() => {
    const cleaned = trail.filter((p) => Number.isFinite(p.latitude) && Number.isFinite(p.longitude));
    if (!cleaned.length) return cleaned;
    if (cleaned.every((p) => p.timestamp)) {
      return [...cleaned].sort((a, b) => {
        const at = new Date(a.timestamp as string).getTime();
        const bt = new Date(b.timestamp as string).getTime();
        return at - bt;
      });
    }
    return cleaned;
  }, [trail]);

  const polyline: L.LatLngTuple[] = useMemo(
    () => orderedTrail.map((p) => [p.latitude, p.longitude] as L.LatLngTuple),
    [orderedTrail],
  );

  const allPoints: L.LatLngTuple[] = useMemo(() => {
    const pts = [...polyline];
    if (store && Number.isFinite(store.latitude) && Number.isFinite(store.longitude)) {
      pts.push([store.latitude, store.longitude]);
    }
    if (customer && Number.isFinite(customer.latitude) && Number.isFinite(customer.longitude)) {
      pts.push([customer.latitude, customer.longitude]);
    }
    return pts;
  }, [polyline, store, customer]);

  const center: L.LatLngTuple = allPoints[0] ?? [27.7172, 85.324]; // Kathmandu fallback

  return (
    <div className={className} style={{ height, position: 'relative', isolation: 'isolate', zIndex: 0 }}>
      <MapContainer
        center={center}
        zoom={13}
        scrollWheelZoom
        style={{ height: '100%', width: '100%', borderRadius: 'inherit', position: 'relative', zIndex: 0 }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds points={allPoints} />

        {polyline.length > 1 ? (
          <>
            <Polyline
              positions={polyline}
              pathOptions={{ color: '#ef4444', weight: 4, opacity: 0.85 }}
            />
            <Marker position={polyline[0]} icon={dotIcon('#3b82f6', 'Start')} />
            <Marker position={polyline[polyline.length - 1]} icon={dotIcon('#22c55e', 'Latest')} />
          </>
        ) : null}

        {store && Number.isFinite(store.latitude) && Number.isFinite(store.longitude) ? (
          <Marker position={[store.latitude, store.longitude]}>
            <Popup>{store.label ?? 'Store'}</Popup>
          </Marker>
        ) : null}

        {customer && Number.isFinite(customer.latitude) && Number.isFinite(customer.longitude) ? (
          <Marker position={[customer.latitude, customer.longitude]}>
            <Popup>{customer.label ?? 'Customer'}</Popup>
          </Marker>
        ) : null}
      </MapContainer>
    </div>
  );
}
